/**
 * Per-surface access for precomputed AI insight cards.
 *
 * - One-line title teasers can be shown to free users (`freeTeaser`).
 * - Discuss / streaming chat always requires Pro (enforced in AssistantContext + server).
 *
 * Flip `freeTeaser` off for a surface to hide callouts/strip until the user upgrades.
 */
export type InsightSurface =
  | 'home_strip'
  | 'home_spend'
  | 'home_loan'
  | 'home_net_worth'
  | 'wealth_trends'
  | 'wealth_portfolio'
  | 'spend_trends'
  | 'spend_calendar'
  | 'spend_transactions'
  | 'debt_overview'
  | 'giving';

export type InsightSurfaceAccess = {
  /** Show the one-line title teaser to non-Pro authenticated users. */
  freeTeaser: boolean;
};

/**
 * Default: all first-wave surfaces tease free users so they see the analysis value.
 * Chat remains Pro-only regardless of these flags.
 */
export const INSIGHT_SURFACE_ACCESS: Record<InsightSurface, InsightSurfaceAccess> = {
  home_strip: { freeTeaser: true },
  home_spend: { freeTeaser: true },
  home_loan: { freeTeaser: true },
  home_net_worth: { freeTeaser: true },
  wealth_trends: { freeTeaser: true },
  wealth_portfolio: { freeTeaser: true },
  spend_trends: { freeTeaser: true },
  spend_calendar: { freeTeaser: true },
  spend_transactions: { freeTeaser: true },
  debt_overview: { freeTeaser: true },
  giving: { freeTeaser: true },
};

export function canViewInsightSurface(
  surface: InsightSurface | string,
  isPro: boolean,
): boolean {
  if (isPro) return true;
  const access = INSIGHT_SURFACE_ACCESS[surface as InsightSurface];
  return access?.freeTeaser === true;
}

/** CTA copy — chat is Pro; free tap still upsells via AssistantContext. */
export function insightDiscussCta(_isPro: boolean): string {
  return 'Explore';
}

/** Visible card line: punchy title, then one-sentence summary. */
export function insightCardLine(card: { title?: string | null; summary?: string | null }): string {
  const title = card.title?.trim();
  if (title) return title;
  return card.summary?.trim() ?? '';
}

export type InsightStoryBeat = {
  label: 'Observation' | 'Interpretation' | 'Impact' | 'Action';
  text: string;
};

function beatText(
  card: {
    observation?: string | null;
    interpretation?: string | null;
    impact?: string | null;
    action?: string | null;
    contextPayload?: Record<string, unknown> | null;
  },
  key: 'observation' | 'interpretation' | 'impact' | 'action',
): string {
  const direct = card[key]?.trim();
  if (direct) return direct;
  const fromPayload = card.contextPayload?.[key];
  return typeof fromPayload === 'string' ? fromPayload.trim() : '';
}

/** Display OIIA for an expanded card. Empty when the list payload has no story yet. */
export function insightStoryBeats(card: {
  observation?: string | null;
  interpretation?: string | null;
  impact?: string | null;
  action?: string | null;
  summary?: string | null;
  contextPayload?: Record<string, unknown> | null;
}): InsightStoryBeat[] {
  const labeled: InsightStoryBeat[] = (
    [
      ['Observation', beatText(card, 'observation')],
      ['Interpretation', beatText(card, 'interpretation')],
      ['Impact', beatText(card, 'impact')],
      ['Action', beatText(card, 'action')],
    ] as const
  )
    .filter(([, text]) => Boolean(text))
    .map(([label, text]) => ({ label, text }));

  if (labeled.length > 0) return labeled;

  const summary = card.summary?.trim();
  if (summary) {
    return [{ label: 'Observation', text: summary }];
  }
  return [];
}
